<script setup lang="ts">
import type { Component } from 'vue'
import { computed, inject, ref } from 'vue'
import { ChevronDown, ChevronUp } from '@lucide/vue'
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger } from 'reka-ui'
import { cn } from '@ds/lib/utils'
import { DSDropdownMenu, DSDropdownMenuContent, DSDropdownMenuLabel, DSDropdownMenuTrigger } from '@ds'
import { SIDEBAR_COLLAPSED } from './const'
import SidebarItem from './SidebarItem.vue'

type Props = {
  icon?: Component
  label: string
  active?: boolean
  /** v-model — expanded-rail accordion open state. Ignored while collapsed. */
  expanded?: boolean
  badge?: number
  isMobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  expanded: false,
})

const emit = defineEmits<{
  (e: 'update:expanded', value: boolean): void
}>()

const collapsed = inject(SIDEBAR_COLLAPSED, ref(false))

const menuOpen = ref(false)
const isHovering = ref(false)

const BADGE_CLASS = 'size-4 rounded-full flex items-center justify-center bg-destructive text-[10px] font-normal text-destructive-foreground'

const triggerClasses = computed(() => cn(
  'relative transition-colors duration-ds-fast ease-ds-standard',
  props.active || menuOpen.value
    ? 'bg-white/15 text-primary-foreground'
    : 'text-primary-foreground/85 hover:bg-white/10 hover:text-primary-foreground',
))
</script>

<template>
  <!--
    Collapsed rail: trigger opens flyout and shows a custom hover label.
    DSTooltip can't be used here because wrapping around a menu trigger breaks click events (see DSPivotLayoutMenu).
  -->
  <DSDropdownMenu
    v-if="collapsed"
    v-model:open="menuOpen"
  >
    <DSDropdownMenuTrigger as-child>
      <SidebarItem
        :icon
        :label
        :active
        :class="triggerClasses"
        :is-mobile
        :data-refid="`sidebarGroup${label.replace(' ', '')}`"
        @mouseenter="isHovering = true"
        @mouseleave="isHovering = false"
        @focus="isHovering = true"
        @blur="isHovering = false"
      >
        <template #badge>
          <div
            v-if="badge"
            :class="cn(BADGE_CLASS, 'absolute -top-0.5 -right-1.5')"
          >
            {{ badge }}
          </div>
        </template>
      </SidebarItem>
    </DSDropdownMenuTrigger>
    <DSDropdownMenuContent
      side="right"
      align="start"
      class="min-w-56"
      :side-offset="15"
    >
      <DSDropdownMenuLabel>{{ label }}</DSDropdownMenuLabel>
      <slot name="menu" />
    </DSDropdownMenuContent>
  </DSDropdownMenu>

  <!--
    Expanded rail: the row toggles an inline accordion instead of a
    popover — children appear indented below it rather than floating.
  -->
  <CollapsibleRoot
    v-else
    :open="expanded"
    class="flex flex-col gap-px"
    @update:open="emit('update:expanded', $event)"
  >
    <CollapsibleTrigger as-child>
      <SidebarItem
        :icon
        :label
        :is-mobile
        class="w-full"
        :class="active ? 'font-semibold' : ''"
      >
        <div
          v-if="badge"
          :class="BADGE_CLASS"
        >
          {{ badge }}
        </div>
        <component
          :is="expanded ? ChevronUp : ChevronDown"
          class="size-4 shrink-0 text-primary-foreground/60 ml-auto"
          aria-hidden="true"
        />
      </SidebarItem>
    </CollapsibleTrigger>
    <CollapsibleContent>
      <slot name="children" />
    </CollapsibleContent>
  </CollapsibleRoot>
</template>
